import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gsap, Power1 } from 'gsap';

import { computeBottomLaneCenters, getCharacterJumpHeightPx } from '@/features/laneChooser/helpers';

export type UseCharacterMovementOptions = {
	answersAtBottom: boolean;
	qRef: React.RefObject<HTMLDivElement | null>;
	laneLayerRef: React.RefObject<HTMLDivElement | null>;
	lanes?: number; // default 4
	laneBottomPct: number; // must match LaneLayer bottomWidthPct (will be clamped similar way)
	animateDuration?: number; // seconds, default 0.25
	jumpDuration?: number; // seconds total (up+down), default 0.35
	onJumpStart?: () => void; // callback at the beginning of a jump
	onJumpEnd?: () => void; // callback when jump animation ends
};

export type UseCharacterMovement = {
	laneIndex: number;
	setLaneIndex: React.Dispatch<React.SetStateAction<number>>;
	moveToLane: (idx: number, animate?: boolean) => void;
	laneCenters: number[];
	jump: () => void;
};

/**
 * useQCharacterMovement
 *
 * Encapsulates logic for computing lane centers, moving the Q character via GSAP,
 * handling keyboard left/right navigation, and keeping position on resize.
 */
export function useCharacterMovement({
	answersAtBottom,
	qRef,
	laneLayerRef,
	lanes = 4,
	laneBottomPct,
	animateDuration = 0.4,
	jumpDuration = 1.6,
	onJumpStart,
	onJumpEnd,
}: UseCharacterMovementOptions): UseCharacterMovement {
	// Start on a centered lane: for odd lanes the middle, for even lanes the right of the two middles
	const [laneIndex, setLaneIndex] = useState<number>(() => {
		const L = Math.max(1, Math.min(4, Math.round(lanes || 4)));
		return L % 2 === 1 ? Math.floor(L / 2) : Math.min(L - 1, L / 2);
	});

	const [jumpHeight, setJumpHeight] = useState<number>(() => getCharacterJumpHeightPx());

	useEffect(() => {
		const updateJumpHeight = () => {
			setJumpHeight((prev) => {
				const next = getCharacterJumpHeightPx();
				return prev === next ? prev : next;
			});
		};

		updateJumpHeight();
		if (typeof window === 'undefined') return;
		window.addEventListener('resize', updateJumpHeight);
		return () => window.removeEventListener('resize', updateJumpHeight);
	}, []);

	// Compute lane center positions at the bottom edge of the lane,
	// applying the same slight within-lane nudges as AnswersPanel so the character
	// aligns visually with the answer boxes (first/last near lane line, middle centered).
	const computeLaneCenters = useCallback((): number[] => {
		const host = laneLayerRef.current;
		if (!host) return [];
		const width = host.clientWidth;
		if (width <= 0) return [];
		return computeBottomLaneCenters({ width, lanes, laneBottomPct });
	}, [laneLayerRef, laneBottomPct, lanes]);

	const laneCenters = useMemo(() => computeLaneCenters(), [computeLaneCenters]);

	const moveToLane = useCallback(
		(idx: number, animate = true) => {
			const centers = computeLaneCenters();
			const node = qRef.current;
			if (!node || centers.length !== lanes) return;
			const clamped = Math.max(0, Math.min(lanes - 1, idx));
			const x = centers[clamped];
			if (animate)
				gsap.to(node, {
					x,
					duration: animateDuration,
					ease: Power1.easeOut,
					overwrite: 'auto',
				});
			else gsap.set(node, { x });
		},
		[computeLaneCenters, qRef, lanes, animateDuration],
	);

	// Clamp lane index if lanes count changes and reposition without animation
	useEffect(() => {
		const clamped = Math.max(0, Math.min(lanes - 1, laneIndex));
		if (clamped !== laneIndex) {
			setLaneIndex(clamped);
		}
		moveToLane(clamped, false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lanes, computeLaneCenters]);

	// Initial placement on first lane and keep centered on resize
	useEffect(() => {
		// place immediately without animation on the chosen initial lane
		moveToLane(laneIndex, false);
		const host = laneLayerRef.current;
		if (!host) return;
		let ro: ResizeObserver | null = null;
		const onResize = () => moveToLane(laneIndex, true);
		if (typeof ResizeObserver !== 'undefined') {
			ro = new ResizeObserver(onResize);
			ro.observe(host);
		} else {
			window.addEventListener('resize', onResize);
		}
		return () => {
			if (ro) ro.disconnect();
			else window.removeEventListener('resize', onResize);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		moveToLane(laneIndex, true);
	}, [laneIndex, moveToLane]);

	const isJumpingRef = useRef<boolean>(false);
	const jumpTlRef = useRef<gsap.core.Timeline | null>(null);

	const doJump = useCallback(() => {
		if (isJumpingRef.current) return;
		const node = qRef.current;
		if (!node) return;
		isJumpingRef.current = true;
		onJumpStart?.();

		// Split jumpDuration into up and down phases (roughly symmetrical)
		const half = Math.max(0.05, jumpDuration / 2);
		const tl = gsap.timeline({
			onComplete: () => {
				isJumpingRef.current = false;
			},
		});
		jumpTlRef.current = tl;
		// Ensure pivot near bottom so rotation feels natural
		gsap.set(node, { transformOrigin: '50% 90%' });
		// Up phase: jump and slight forward tilt. Positive rotation is clockwise in CSS.
		// Rotate only to the right regardless of last movement direction.
		const jumpRotation = 90;
		tl.to(node, {
			y: `-=${jumpHeight}`,
			rotation: jumpRotation,
			scale: 0.9,
			duration: half,
			ease: Power1.easeOut,
		});
		if (answersAtBottom) {
			tl.set(node, { zIndex: 2 });
		}
		// Down phase: fall and smoothly return rotation to neutral
		tl.to(node, {
			y: `+=${jumpHeight}`,
			rotation: 0,
			scale: 1,
			duration: half,
			ease: Power1.easeIn,
		});
		tl.call(() => {
			gsap.set(node, { rotation: 0, scale: 1 });
			onJumpEnd?.();
		});
	}, [answersAtBottom, jumpHeight, jumpDuration, qRef, onJumpStart, onJumpEnd]);

	useEffect(() => {
		return () => {
			if (jumpTlRef.current) {
				jumpTlRef.current.kill();
				jumpTlRef.current = null;
			}
			isJumpingRef.current = false;
		};
	}, []);

	return { laneIndex, setLaneIndex, moveToLane, laneCenters, jump: doJump };
}

export default useCharacterMovement;
